import ConfirmationDialog from 'Components/ConfirmationDialog';
import { fm } from 'Components/FormatMessage';
import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import InputHandler from 'Components/InputHandler';
import InputHistoryAcceptor from 'Components/InputHistoryAcceptor';
import PlatformConditional from 'Components/PlatformConditional';
import { calculatePackYearsFromDocuments } from 'neuro-calculation-commons';
import { exists, parsePatientDemographics, partialDateToValue } from 'neuro-utils';
import { pick } from 'ramda';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { getFieldCondition } from 'Routes/Background/utils';
import { useAppSelector } from 'Store/index';
import { mapDocumentToNeuroDocument } from 'Utility/documentHandling';
import { getKeys } from 'Utility/object';
import { capitalize } from 'Utility/string';
import EventList from './EventList';
import { rowData as allRowData } from './rowData';
import { sharedRowData } from './sharedRowData';

import type { RowDataOther } from './rowData';
import type { ICompactVaultUser } from 'neuro-data-structures';
import type { TBackgroundDocument } from 'Routes/Background/utils';

const optionFormatter =
  (opt: number | string) =>
  (name: number | string): React.JSX.Element => {
    return fm(`background.${opt}.${name}`);
  };

const FormInputRow = ({
  documents,
  formData,
  title,
  subject,
  type,
  options,
  placeholder,
  precision,
  optionFormat,
  condition,
  sizing,
  id,
  otherProps,
}: IFormInputRow & RowDataOther): React.JSX.Element | null => {
  return condition === undefined || condition ? (
    <FormRow
      id={id}
      title={`background.${title}`}
      info={subject === 'educationYears' ? fm('background.educationYearsInfo') : undefined}
      description={
        subject === 'workAbilityScore.workAbilityComparedToLifetimeBest'
          ? fm('background.workAbilityScore.info', { br: '\n' })
          : undefined
      }
    >
      <InputHistoryAcceptor
        documents={documents}
        type={type}
        name={subject}
        editing={true}
        formData={formData}
        options={options}
        placeholder={placeholder}
        precision={precision}
        optionFormatter={
          type === 'Radio' || type === 'Checkbox'
            ? optionFormat
              ? optionFormatter(`opts.${optionFormat}`)
              : optionFormatter(`opts.${subject}`)
            : undefined
        }
        sizing={sizing}
        {...otherProps}
      />
    </FormRow>
  ) : null;
};

const BackgroundFormDynamic = ({ formData, documents, isShared }: IOwnProps): React.JSX.Element => {
  const previousDocs = documents.filter(
    d => partialDateToValue(d.date) < partialDateToValue(formData.document.date) && d._editing === false,
  );

  const onDelete = (name: keyof IFormData<IDocumentWithTypeV2<'background'>>['document'], index: number): void => {
    const array = formData.document[name];
    const data = Array.isArray(array) ? [...array] : [];
    data.splice(index, 1);
    formData.onChange && formData.onChange({ [name]: data });
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState<boolean>(false);
  const [deleteDialogData, setDeleteDialogData] = React.useState<
    { name: keyof IFormData<IDocumentWithTypeV2<'background'>>['document']; index: number } | undefined
  >(undefined);

  const openDeleteDialog = (
    name: keyof IFormData<IDocumentWithTypeV2<'background'>>['document'],
    index: number,
  ): void => {
    setDeleteDialogOpen(true);
    setDeleteDialogData({ name, index });
  };

  const deleteCancelCallback = (): void => {
    setDeleteDialogOpen(false);
    setDeleteDialogData(undefined);
  };

  const deleteConfirmCallback = (): void => {
    deleteDialogData && onDelete(deleteDialogData.name, deleteDialogData.index);
    setDeleteDialogOpen(false);
    setDeleteDialogData(undefined);
  };

  const selectedPlatform = useSelector((s: { session: ISessionStore }) => s.session.platforms?.selected);

  // If document is shared (My document), pick only shared fields from row data
  // Currently only supports S&R and Parkinson
  const rowData =
    isShared && selectedPlatform
      ? {
          ...allRowData,
          [selectedPlatform]: Object.fromEntries(
            getKeys(sharedRowData[selectedPlatform] ?? {}).map(key => {
              if (allRowData[selectedPlatform][key]) {
                return [key, pick(sharedRowData[selectedPlatform]?.[key] ?? [], allRowData[selectedPlatform][key])];
              }
              return [key, allRowData[selectedPlatform][key]];
            }),
          ),
        }
      : allRowData;

  const isFemale = (patientData: IPatientUI): boolean => {
    const { ssn, ssnLocalization } = patientData;
    if (!ssn || !ssnLocalization) return false;
    return parsePatientDemographics(ssnLocalization, { ssn })?.gender === 'female';
  };

  const onChangeMgForm = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;

    const value = values[name];

    if (name.includes('workAbilityScore')) {
      const fieldPath = name.split('.');
      const field = fieldPath[0];
      const subField = fieldPath[1];
      if (!field || !subField) return;
      if (subField === 'workAbilityComparedToLifetimeBest') {
        // Changing `workAbilityComparedToLifetimeBest` clears `reductionReason` and `reductionType`
        formData.onChange?.({
          workAbilityScore: { workAbilityComparedToLifetimeBest: value, reductionReason: null, reductionType: null },
        });
      } else {
        formData.onChange?.({
          [field]: { ...formData.document.workAbilityScore, [subField]: value },
        });
      }
    } else if (name && (exists(value) || value === false)) {
      formData.onChange?.({ [name]: value });
    }
  };

  const calculatedPackYears = React.useMemo(() => {
    const backgroundNeuroDocs = documents.map(d => mapDocumentToNeuroDocument(d));
    return calculatePackYearsFromDocuments(backgroundNeuroDocs);
  }, [documents]);

  const patientData = useAppSelector(s => s.patient.data);

  return (
    <React.Fragment>
      <FormSection>
        <FormRow title="general.date">
          <InputHandler
            type="PartialDate"
            name="date"
            editing={true}
            formData={formData}
            dateDefault="now"
            isNotCancellable={true}
          />
        </FormRow>
      </FormSection>

      <PlatformConditional platform={['epilepsy', 'sleepApnea', 'parkinson', 'mgravis']}>
        <React.Fragment>
          {selectedPlatform &&
            getKeys(rowData[selectedPlatform]).map((title, ind) => {
              return (
                <FormSection
                  key={`${String(title)}${ind}`}
                  header={
                    title === 'basicInformation'
                      ? ''
                      : title === 'snus' || title === 'nicotineProducts'
                        ? `background.${title}Title`
                        : `background.${String(title)}`
                  }
                >
                  <>
                    {getKeys(rowData[selectedPlatform][title]).map((key, index) => {
                      if (selectedPlatform === 'sleepApnea' && key === 'packYears') {
                        if (
                          !(
                            formData.document.smoker === 'currentSmoker' || formData.document.smoker === 'formerSmoker'
                          ) ||
                          !formData.document.smokingStartYear ||
                          !exists(formData.document.cigaretteAmountPerDay)
                        )
                          return;
                        const packYearsData = rowData[selectedPlatform][title]['packYears'];
                        return (
                          <FormRow
                            title={packYearsData.placeholder}
                            key="index"
                            info={<FormattedMessage id={packYearsData.info} />}
                          >
                            <div style={{ marginTop: '1rem', fontWeight: 600 }}>{calculatedPackYears}</div>
                          </FormRow>
                        );
                      }
                      return (
                        <FormInputRow
                          key={index}
                          documents={documents}
                          formData={{
                            document: formData.document,
                            onChange: selectedPlatform === 'mgravis' ? onChangeMgForm : formData.onChange,
                          }}
                          title={key === 'familyMembersParkinson' ? 'relatives' : `${String(key)}`}
                          type={rowData[selectedPlatform][title][key as string]?.['type']}
                          subject={key as string}
                          options={rowData[selectedPlatform][title][key as string]?.['options'] ?? undefined}
                          placeholder={rowData[selectedPlatform][title][key as string]?.['placeholder'] ?? undefined}
                          optionFormat={rowData[selectedPlatform][title][key as string]?.['optionFormat'] ?? undefined}
                          condition={getFieldCondition(key as keyof IBackground, formData.document, selectedPlatform)}
                          otherProps={rowData[selectedPlatform][title][key as string]?.['otherProps'] ?? undefined}
                        />
                      );
                    })}
                    {title === 'drivingHealth' &&
                    !isShared &&
                    formData.document.driversLicense === 'yes' &&
                    formData.document.driversLicenseGroup?.includes('R1') ? (
                      <EventList
                        formData={formData}
                        previousDocs={previousDocs}
                        openDeleteDialog={openDeleteDialog}
                        title={'background.addDrivingBanR1'}
                        addButton={'background.addDrivingBanR1'}
                        eventKey={'drivingBanR1s'}
                        datesDontOverlap={true}
                      />
                    ) : (
                      <></>
                    )}
                    {title === 'drivingHealth' &&
                    !isShared &&
                    formData.document.driversLicense === 'yes' &&
                    formData.document.driversLicenseGroup?.includes('R2') ? (
                      <EventList
                        formData={formData}
                        previousDocs={previousDocs}
                        openDeleteDialog={openDeleteDialog}
                        title={'background.addDrivingBanR2'}
                        addButton={'background.addDrivingBanR2'}
                        eventKey={'drivingBanR2s'}
                        datesDontOverlap={true}
                      />
                    ) : (
                      <></>
                    )}
                    {selectedPlatform === 'sleepApnea' &&
                    !isShared &&
                    title === 'drivingHealth' &&
                    formData.document.driversLicense === 'yes' &&
                    formData.document.professionalDriving === 'yes' ? (
                      <EventList
                        formData={formData}
                        previousDocs={previousDocs}
                        openDeleteDialog={openDeleteDialog}
                        title={'background.addNoProRequirements'}
                        addButton={'general.new'}
                        eventKey={'noProDrivingRequirements'}
                        datesDontOverlap={true}
                      />
                    ) : (
                      <></>
                    )}
                    {patientData &&
                    isFemale(patientData) &&
                    selectedPlatform === 'mgravis' &&
                    title === 'employment' ? (
                      <EventList
                        formData={formData}
                        previousDocs={previousDocs}
                        openDeleteDialog={openDeleteDialog}
                        title={'background.pregnancies'}
                        addButton={'background.addPregnancy'}
                        eventKey={'pregnancies'}
                        datesDontOverlap={true}
                      />
                    ) : (
                      <></>
                    )}
                  </>
                </FormSection>
              );
            })}
          <ConfirmationDialog
            open={deleteDialogOpen}
            text={fm('general.reallyWantToDelete')}
            confirm={{ callback: deleteConfirmCallback }}
            cancel={{ callback: deleteCancelCallback }}
          />
        </React.Fragment>
      </PlatformConditional>

      <PlatformConditional platform={['ms', 'huntington', 'sma', 'dmd', 'polyneuro']}>
        <FormSection>
          <PlatformConditional platform="ms">
            <React.Fragment>
              <div style={{ overflow: 'hidden' }}>
                {Object.keys(rowData.ms).map((key, index) => {
                  const options = Array.isArray(rowData.ms[key]['options'])
                    ? rowData.ms[key]['options'].filter((option: string) => {
                        return key === 'education' &&
                          formData.document.education?.includes('postSecondaryQualification')
                          ? true
                          : option !== 'postSecondaryQualification';
                      })
                    : undefined;

                  return (
                    <React.Fragment key={index}>
                      {['pregnancy', 'nursing'].includes(key) ? (
                        <EventList
                          formData={formData}
                          previousDocs={previousDocs}
                          openDeleteDialog={openDeleteDialog}
                          title={`background.${key === 'pregnancy' ? 'pregnancies' : key}`}
                          addButton={`background.add${capitalize(key)}`}
                          eventKey={`${key === 'pregnancy' ? 'pregnancies' : key}` as keyof IBackground}
                          datesDontOverlap={true}
                        />
                      ) : (
                        <FormInputRow
                          documents={documents}
                          formData={formData}
                          title={key === 'familyMembersAnamnesisMS' ? 'relatives' : key}
                          type={rowData.ms[key]['type']}
                          subject={key}
                          options={options}
                          placeholder={rowData.ms[key]['placeholder'] ?? undefined}
                          optionFormat={rowData.ms[key]['optionFormat'] ?? undefined}
                          condition={getFieldCondition(key as keyof IBackground, formData.document, 'ms')}
                          id={key + index}
                          otherProps={rowData.ms[key]?.['otherProps'] ?? undefined}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <ConfirmationDialog
                open={deleteDialogOpen}
                text={fm('general.reallyWantToDelete')}
                confirm={{ callback: deleteConfirmCallback }}
                cancel={{ callback: deleteCancelCallback }}
              />
            </React.Fragment>
          </PlatformConditional>

          <PlatformConditional platform="huntington">
            <div style={{ overflow: 'hidden' }}>
              {Object.keys(rowData.huntington).map((key, index) => {
                const options = Array.isArray(rowData.huntington[key]['options'])
                  ? rowData.huntington[key]['options'].filter((option: string) => {
                      return key === 'education' && formData.document.education?.includes('postSecondaryQualification')
                        ? true
                        : option !== 'postSecondaryQualification';
                    })
                  : undefined;

                /** Mostly the key itself, but exceptions in a couple cases. */
                const getRowTitle = (key: string): string => {
                  switch (key) {
                    case 'familyMembersAnamnesisHuntington':
                      return 'relatives';
                    case 'contactPermit':
                      return 'contactPermitForResearch';
                    default:
                      return key;
                  }
                };

                return (
                  <FormInputRow
                    key={index}
                    documents={documents}
                    formData={formData}
                    title={getRowTitle(key)}
                    type={rowData.huntington[key]['type']}
                    subject={key}
                    options={options}
                    placeholder={rowData.huntington[key]['placeholder'] ?? undefined}
                    optionFormat={rowData.huntington[key]['optionFormat'] ?? undefined}
                    condition={getFieldCondition(key as keyof IBackground, formData.document, 'huntington')}
                  />
                );
              })}
            </div>
          </PlatformConditional>
          <PlatformConditional platform="sma">
            {getKeys(rowData.sma).map((key, index) => {
              const getRowTitle = (key: string | number): string => {
                switch (key) {
                  case 'familyMembers':
                    return 'relatives';
                  default:
                    return `${key}`;
                }
              };

              return (
                <FormInputRow
                  key={index}
                  documents={documents}
                  formData={formData}
                  title={getRowTitle(key)}
                  type={rowData.sma[key]?.['type']}
                  subject={`${key}`}
                  options={rowData.sma[key]?.['options'] ?? undefined}
                  placeholder={rowData.sma[key]?.['placeholder'] ?? undefined}
                  optionFormat={rowData.sma[key]?.['optionFormat'] ?? undefined}
                  condition={getFieldCondition(key as keyof IBackground, formData.document, 'sma')}
                  otherProps={rowData.sma[key]?.['otherProps'] ?? undefined}
                />
              );
            })}
          </PlatformConditional>
          <PlatformConditional platform="dmd">
            {getKeys(rowData.dmd).map((key, index) => {
              const getRowTitle = (key: string | number): string => {
                switch (key) {
                  case 'familyMembers':
                    return 'relatives';
                  default:
                    return `${key}`;
                }
              };

              return (
                <FormInputRow
                  key={index}
                  documents={documents}
                  formData={formData}
                  title={getRowTitle(key)}
                  type={rowData.dmd[key]?.['type']}
                  subject={`${key}`}
                  options={rowData.dmd[key]?.['options'] ?? undefined}
                  placeholder={rowData.dmd[key]?.['placeholder'] ?? undefined}
                  optionFormat={rowData.dmd[key]?.['optionFormat'] ?? undefined}
                  condition={getFieldCondition(key as keyof IBackground, formData.document, 'dmd')}
                  otherProps={rowData.dmd[key]?.['otherProps'] ?? undefined}
                />
              );
            })}
          </PlatformConditional>
          <PlatformConditional platform="polyneuro">
            {getKeys(rowData.polyneuro).map((key, index) => {
              const getRowTitle = (key: string | number): string => {
                switch (key) {
                  default:
                    return `${key}`;
                }
              };

              return (
                <FormInputRow
                  key={index}
                  documents={documents}
                  formData={formData}
                  title={getRowTitle(key)}
                  type={rowData.polyneuro[key]?.['type']}
                  subject={`${key}`}
                  options={rowData.polyneuro[key]?.['options'] ?? undefined}
                  placeholder={rowData.polyneuro[key]?.['placeholder'] ?? undefined}
                  optionFormat={rowData.polyneuro[key]?.['optionFormat'] ?? undefined}
                  condition={getFieldCondition(key as keyof IBackground, formData.document, 'polyneuro')}
                  otherProps={rowData.polyneuro[key]?.['otherProps'] ?? undefined}
                />
              );
            })}
          </PlatformConditional>
        </FormSection>
      </PlatformConditional>
    </React.Fragment>
  );
};

export interface IFormInputRow extends Omit<IOwnProps, 'users' | 'isShared'> {
  title: string;
  subject: string;
  type: 'Radio' | 'NumberField' | 'Checkbox' | 'PartialDate';
  options?: (string | number)[];
  placeholder?: string;
  precision?: number;
  optionFormat?: string;
  /** Whether to render the input row or not. */
  condition?: boolean;
  sizing?: { left: number; right: number };
  id?: string;
  dependentFieldsList?: (value: TFieldValue, prevValue: TFieldValue) => string[];
}

interface IOwnProps {
  formData: IFormData<IDocumentWithTypeV2<'background'>>;
  documents: TBackgroundDocument[];
  users: ICompactVaultUser[];
  // If editing/viewing My documents
  isShared?: boolean;
}

export default BackgroundFormDynamic;
